import React from 'react';
import { Modal } from 'antd';
import COButton from '../COButton';
import styles from './RequestIngredientModal.module.scss';
import {
  ButtonLabels,
  CreateBrandModalProps,
} from 'enums/CreateBrandModalEnums';
import InputField from '../InputField';
import { Formik, Form } from 'formik';
import ProductsService from 'services/ProductsService/products.service';
import { ReactComponent as MessageIcon } from 'assets/ColorIcons/SVG/group_58673.svg';
import {
  ingredientModalConstants,
  ingredientValidationSchema,
  ModalProps,
  packagingValidationSchema,
} from './RequestIngredientModalConstants';
import { useParams } from 'react-router-dom';
import COSelect from '../COSelect';
import { unitOptions } from 'views/CreateProductFlow/RawMaterial/CreateProductFlowConstants';
import { modalCloseIcon } from '../OrganizationModal';
import { FacilityProps } from 'enums/ProcessingFacilityCreateProductFlowEnums';
import { CommodityTypes } from 'constants/FarmboxConstants/stage0.constants';

interface RequestMaterialModalProps {
  showModalState: boolean;
  handleModalCancel: () => void;
  handleModalConfirm: () => void;
  isPackageMaterial?: boolean;
  isOtherMaterial?: boolean;
  isScopeThree?: boolean;
  isFertilisers?: boolean;
  isFarmbox?: boolean;
  isFeed?: boolean;
}

interface RequestIngredientFormValues {
  ingredientName: string;
  ingredientOrigin: string;
  additionalDetails: string;
  quantity: string;
  unit?: string;
  have_sub_ingredient?: boolean;
  sub_ingredient_list: string;
}

const RequestIngredientModal: React.FC<RequestMaterialModalProps> = ({
  showModalState,
  handleModalCancel,
  isPackageMaterial,
  isOtherMaterial,
  isScopeThree,
  isFertilisers,
  isFarmbox,
  isFeed,
}: RequestMaterialModalProps) => {
  const { id } = useParams();
  const { requestIngredient, buttonLoading } = ProductsService();

  const initialValues: RequestIngredientFormValues = {
    ingredientName: '',
    ingredientOrigin: '',
    additionalDetails: '',
    quantity: '',
    unit: undefined,
    have_sub_ingredient: undefined,
    sub_ingredient_list: '',
  };
  const { PACKAGING, OTHER, FERTILISERS, FEED, INGREDIENT } = FacilityProps;

  const {
    HEADING_PACKAGING,
    HEADING_OTHER,
    HEADING_FERTILISER,
    HEADING_FEED,
    HEADING,
    THANK_YOU_PACKAGING_TEXT,
    THANK_YOU_OTHER_TEXT,
    THANK_YOU_FERTILISER_TEXT,
    THANK_YOU_FEED_TEXT,
    THANK_YOU_INGREDIENT_TEXT,
  } = ModalProps;

  const thankYoutext =
    (isPackageMaterial && THANK_YOU_PACKAGING_TEXT) ||
    (isOtherMaterial && THANK_YOU_OTHER_TEXT) ||
    (isFertilisers && THANK_YOU_FERTILISER_TEXT) ||
    (isFeed && THANK_YOU_FEED_TEXT) ||
    THANK_YOU_INGREDIENT_TEXT;
  const {
    PACKAGING_MATERIAL_NAME_PLACEHOLDER,
    OTHER_MATERIAL_NAME_PLACEHOLDER,
    FERTILISER_MATERIAL_NAME_PLACEHOLDER,
    FEED_MATERIAL_NAME_PLACEHOLDER,
    INGREDIENT_NAME_PLACEHOLDER,
    INGREDIENT_NAME,
    INGREDIENT_ORIGIN,
    INGREDIENT_ORIGIN_PLACEHOLDER,
    QUANTITY,
    QUANTITY_ORIGIN_PLACEHOLDER,
    UNIT,
    UNIT_PLACEHOLDER,
    HAVE_SUB_INGREDIENT,
    HAVE_SUB_INGREDIENT_PLACEHOLDER,
    SUB_INGREDIENT_LIST,
    SUB_INGREDIENT_LIST_PLACEHOLDER,
    ADDITIONAL_DETAILS,
    ADDITIONAL_DETAILS_PLACEHOLDER,
  } = ingredientModalConstants;

  let materialTypes = INGREDIENT;
  let label = '';
  let desiredHeading = HEADING;

  switch (true) {
    case isPackageMaterial:
      materialTypes = PACKAGING;
      label = PACKAGING_MATERIAL_NAME_PLACEHOLDER;
      desiredHeading = HEADING_PACKAGING;
      break;
    case isOtherMaterial:
      materialTypes = OTHER;
      label = OTHER_MATERIAL_NAME_PLACEHOLDER;
      desiredHeading = HEADING_OTHER;
      break;
    case isFertilisers:
      materialTypes = FERTILISERS;
      label = FERTILISER_MATERIAL_NAME_PLACEHOLDER;
      desiredHeading = HEADING_FERTILISER;
      break;
    case isFeed:
      materialTypes = FEED;
      label = FEED_MATERIAL_NAME_PLACEHOLDER;
      desiredHeading = HEADING_FEED;
      break;
    default:
      label = INGREDIENT_NAME_PLACEHOLDER;
  }

  const handleFormSubmission = async (values: RequestIngredientFormValues) => {
    try {
      if (id) {
        let additionalDetails = {};
        if (!(isPackageMaterial || isOtherMaterial)) {
          additionalDetails = {
            quantity: values.quantity,
            unit: values.unit,
            have_sub_ingredient: values.have_sub_ingredient,
            sub_ingredient_list: values.sub_ingredient_list,
          };
        }
        const desiredId = isScopeThree
          ? { location_id: id }
          : isFarmbox
          ? { commodity_id: id }
          : { product_id: id };
        await requestIngredient({
          name: values.ingredientName,
          origin: values.ingredientOrigin,
          material_type: materialTypes,
          additional_details: values.additionalDetails,
          ...desiredId,
          ...(isFarmbox &&
            isFeed && { commodity_type: CommodityTypes.LIVE_STOCK }),
          ...(isFarmbox &&
            !isFeed && { commodity_type: CommodityTypes.AGRICULTURE }),
          ...additionalDetails,
        });
      }
      handleModalCancel();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Modal
      destroyOnClose={true}
      maskClosable={false}
      width={CreateBrandModalProps.MODAL_WIDTH}
      footer={null}
      className={styles.modalContainer}
      open={showModalState}
      closeIcon={modalCloseIcon}
      onCancel={handleModalCancel}
      centered>
      <div className={styles.formContainer}>
        <div className={styles.headerContainer}>
          <MessageIcon />
          <h2>{desiredHeading}</h2>
        </div>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={
            isPackageMaterial || isOtherMaterial
              ? packagingValidationSchema
              : ingredientValidationSchema
          }
          onSubmit={handleFormSubmission}>
          {({ values }) => (
            <Form>
              <div className={styles.fieldContainer}>
                <InputField
                  name={INGREDIENT_NAME}
                  placeholderstring={label}
                  label={label}
                />
              </div>
              <div className={styles.fieldContainer}>
                <InputField
                  name={INGREDIENT_ORIGIN}
                  placeholderstring={INGREDIENT_ORIGIN_PLACEHOLDER}
                  label={INGREDIENT_ORIGIN_PLACEHOLDER}
                />
              </div>
              {!(isPackageMaterial || isOtherMaterial) && (
                <>
                  <div className={styles.inlineField}>
                    <InputField
                      name={QUANTITY}
                      placeholderstring={QUANTITY_ORIGIN_PLACEHOLDER}
                      label={QUANTITY_ORIGIN_PLACEHOLDER}
                    />
                    <div>
                      <COSelect
                        name={UNIT}
                        label={UNIT_PLACEHOLDER}
                        placeholderstring={UNIT_PLACEHOLDER}
                        options={unitOptions}
                      />
                    </div>
                  </div>
                  <div className={styles.fieldContainer}>
                    <COSelect
                      name={HAVE_SUB_INGREDIENT}
                      label={HAVE_SUB_INGREDIENT_PLACEHOLDER}
                      placeholderstring={HAVE_SUB_INGREDIENT_PLACEHOLDER}
                      options={[
                        {
                          label: 'Yes',
                          value: true,
                        },
                        {
                          label: 'No',
                          value: false,
                        },
                      ]}
                    />
                  </div>
                </>
              )}
              {values.have_sub_ingredient && (
                <div className={styles.fieldContainer}>
                  <InputField
                    textarea
                    rows={5}
                    name={SUB_INGREDIENT_LIST}
                    placeholderstring={SUB_INGREDIENT_LIST_PLACEHOLDER}
                    label={SUB_INGREDIENT_LIST_PLACEHOLDER}
                  />
                </div>
              )}
              <div className={styles.fieldContainer}>
                <InputField
                  textarea
                  rows={5}
                  name={ADDITIONAL_DETAILS}
                  placeholderstring={ADDITIONAL_DETAILS_PLACEHOLDER}
                  label={ADDITIONAL_DETAILS_PLACEHOLDER}
                />
              </div>
              <h3 className={styles.thankText}>{thankYoutext}</h3>

              <div className={styles.buttonContainer}>
                <COButton submit loading={buttonLoading}>
                  {ModalProps.SUBMIT}
                </COButton>
                <COButton secondary onClick={handleModalCancel}>
                  {ButtonLabels.CANCEL}
                </COButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
};

export default RequestIngredientModal;
